import { useCallback, useEffect, useState } from 'react';
import { orgsApi } from '../api/orgs';
import type { OrgListEntry } from '../api/orgs';

export interface UseOrgsResult {
  /** The current identity's ACTIVE org memberships (ADR-0010 Phase 2). */
  orgs: OrgListEntry[];
  /** True while the initial (or a `refresh()`-triggered) fetch is in flight. */
  isLoading: boolean;
  error: string | null;
  /** Re-fetches `GET /api/orgs` — used after a 403 `NOT_A_MEMBER` switch failure. */
  refresh: () => Promise<void>;
}

/**
 * Fetches the current identity's active org memberships (`GET /api/orgs`).
 * Consumers gate multi-org UI (the nav switcher, the post-login picker) on
 * `orgs.length >= 2` — single-org identities must see no switcher at all.
 */
export function useOrgs(): UseOrgsResult {
  const [orgs, setOrgs] = useState<OrgListEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await orgsApi.list();
      setOrgs(response.orgs);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load organizations');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { orgs, isLoading, error, refresh };
}
